import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Paper,
  Chip,
  Grid
} from '@material-ui/core';
import ChipInput from 'components/shared/ChipInput';
import strings from '../../../strings';

const SectionCard = ({ title, children }) => (
  <Paper
    elevation={1}
    style={{
      padding: 16,
      marginBottom: 16,
      borderRadius: 8,
      border: "1px solid #e3f2fd",
      backgroundColor: "var(--bg-canvas)",
    }}
  >
    <Typography variant="h6" style={{ fontWeight: 600, color: "var(--rox-lilac-ink)", marginBottom: 16 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const ControlledBackgroundCriteriaForm = ({ value, onUpdatePolicy }) => {
  const handleToggle = (field, checked) => {
    if (onUpdatePolicy) {
      onUpdatePolicy(field, checked);
    }
  };

  const handleFieldChange = (field, newValue) => {
    if (onUpdatePolicy) {
      onUpdatePolicy(field, newValue);
    }
  };

  return (
    <Box>
      <SectionCard title={strings.MARKETPLACE.BACKGROUND_CONFIGURATION}>
        {/* Criminal Records Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
            {strings.MARKETPLACE.CRIMINAL_RECORDS}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                label={strings.MARKETPLACE.REQUIRE_CLEAN_CRIMINAL}
                control={
                  <Switch
                    color="primary"
                    checked={value?.requiredCleanCriminalRecord || false}
                    onChange={(e) => handleToggle('requiredCleanCriminalRecord', e.target.checked)}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={strings.MARKETPLACE.LOOK_BACK_YEARS}
                variant="outlined"
                size="small"
                type="number"
                placeholder="e.g. 5"
                value={value?.criminalRecordsLookBackYears || ""}
                onChange={(e) =>
                  handleFieldChange('criminalRecordsLookBackYears', e.target.value ? parseInt(e.target.value, 10) : null)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={6}>
              <ChipInput
                label={strings.MARKETPLACE.EXCLUDED_CASE_TYPES}
                values={value?.excludedCriminalRecordCaseTypes || []}
                onChange={(v) => handleFieldChange('excludedCriminalRecordCaseTypes', v)}
                placeholder={strings.MARKETPLACE.CASE_TYPES_PLACEHOLDER}
                helperText="Type a case type and select from the dropdown to add it"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInput
                label={strings.MARKETPLACE.EXCLUDED_DISPOSITIONS}
                values={value?.excludedCriminalRecordDispositions || []}
                onChange={(v) => handleFieldChange('excludedCriminalRecordDispositions', v)}
                placeholder={strings.MARKETPLACE.DISPOSITIONS_PLACEHOLDER}
                helperText="Type a disposition and select from the dropdown to add it"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Liens and Judgments Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
            {strings.MARKETPLACE.LIENS_JUDGMENTS}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                label={strings.MARKETPLACE.REQUIRE_CLEAN_LIENS}
                control={
                  <Switch
                    color="primary"
                    checked={value?.requiredCleanLiensAndJudgements || false}
                    onChange={(e) => handleToggle('requiredCleanLiensAndJudgements', e.target.checked)}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={strings.MARKETPLACE.MAXIMUM_FILING_QUANTITY}
                variant="outlined"
                size="small"
                type="number"
                placeholder="e.g. 3"
                value={value?.liensAndJudgmentFilingMaxAmount || ""}
                onChange={(e) =>
                  handleFieldChange('liensAndJudgmentFilingMaxAmount', e.target.value ? parseInt(e.target.value, 10) : null)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <ChipInput
                label={strings.MARKETPLACE.EXCLUDED_FILING_TYPES}
                values={value?.excludedLiensAndJudgmentFilingTypes || []}
                onChange={(v) => handleFieldChange('excludedLiensAndJudgmentFilingTypes', v)}
                placeholder={strings.MARKETPLACE.FILING_TYPES_PLACEHOLDER}
                helperText="Type a filing type and select from the dropdown to add it"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Bankruptcies Section */}
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
            {strings.MARKETPLACE.BANKRUPTCIES}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                label={strings.MARKETPLACE.REQUIRE_CLEAN_BANKRUPTCIES}
                control={
                  <Switch
                    color="primary"
                    checked={value?.requiredCleanBankruptcies || false}
                    onChange={(e) => handleToggle('requiredCleanBankruptcies', e.target.checked)}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={strings.MARKETPLACE.LOOK_BACK_YEARS}
                variant="outlined"
                size="small"
                type="number"
                placeholder="e.g. 7"
                value={value?.bankruptciesLookBackYears || ""}
                onChange={(e) =>
                  handleFieldChange('bankruptciesLookBackYears', e.target.value ? parseInt(e.target.value, 10) : null)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={6}>
              <ChipInput
                label={strings.MARKETPLACE.EXCLUDED_CHAPTERS}
                values={value?.excludedBankruptciesChapters || []}
                onChange={(v) => handleFieldChange('excludedBankruptciesChapters', v)}
                placeholder={strings.MARKETPLACE.CHAPTERS_PLACEHOLDER}
                helperText="Type a chapter and select from the dropdown to add it"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInput
                label={strings.MARKETPLACE.EXCLUDED_DISPOSITIONS}
                values={value?.excludedBankruptciesDispositions || []}
                onChange={(v) => handleFieldChange('excludedBankruptciesDispositions', v)}
                placeholder={strings.MARKETPLACE.BANKRUPTCIES_DISPOSITIONS_PLACEHOLDER}
                helperText="Type a disposition and select from the dropdown to add it"
              />
            </Grid>
          </Grid>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default ControlledBackgroundCriteriaForm;